/* The landing page. Answers "what needs me today" before anything else, which is
   why Needs attention sits above the registers rather than below them. */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthProvider';
import { useDepotScope, scoped } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { useOnline } from '../../hooks/useOnline';
import { buildExpiries, dueWithin } from '../../lib/expiries';
import { isOffboarding, isOnboarding } from '../../lib/lifecycle';
import { countdown, expiryTone, fmtDate, greeting, money } from '../../lib/format';
import {
  headcount,
  isDormant,
  isRealPerson,
  isUnclassified,
  offboardingGaps,
  visibleLicences,
} from '../../lib/registers';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  EmptyState,
  LoadError,
  Meter,
  Panel,
  StatCard,
  StatGrid,
  TableSkeleton,
} from '../ui';
import { Tile, Tiles } from './Tile';
import s from './screens.module.scss';

export function DashboardScreen() {
  const svc = useServices();
  const { user } = useAuth();
  const { scope, setScope } = useDepotScope();
  const online = useOnline();
  const qc = useQueryClient();

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const locations = useQuery({ queryKey: qk.locations, queryFn: () => svc.locations.all() });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const skus = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });
  const domains = useQuery({ queryKey: qk.domains, queryFn: () => svc.domains.list() });
  const applications = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });
  const articles = useQuery({ queryKey: qk.articles, queryFn: () => svc.articles.list() });
  const tiles = useQuery({ queryKey: qk.statusTiles, queryFn: () => svc.status.tiles() });
  const audit = useQuery({ queryKey: qk.audit, queryFn: () => svc.audit.list(8) });
  const backups = useQuery({ queryKey: qk.backups, queryFn: () => svc.backups.jobs() });

  /* Seconds since the button was pressed. A counter rather than a bar, because
     a bar would have to invent a proportion — the run takes as long as the
     tenant takes to answer, and pretending to know how far through it is would
     be a worse lie than a number going up. */
  const [elapsed, setElapsed] = useState(0);

  const sync = useMutation({

    meta: { ok: 'Sync finished. The registers are up to date.' },
    onMutate: () => setElapsed(0),
    mutationFn: () => svc.sync.trigger(),
    onSuccess: () => void qc.invalidateQueries(),
  });

  useEffect(() => {
    if (!sync.isPending) return;
    const id = setInterval(() => setElapsed((n: number) => n + 1), 1000);
    return () => clearInterval(id);
  }, [sync.isPending]);


  /* The sync tile is picked out of the pushed-tile list and shown beside the
     button instead. It was the only tile on the board, so it sat alone under a
     section heading in the middle of the page, describing the very thing the
     button above it claims to do. The rest of the list keeps working for
     anything else a script pushes. */
  const syncTile = (tiles.data ?? []).find(
    (t) => /sync/i.test(t.label) || /sync/i.test(t.src),
  );
  const otherTiles = (tiles.data ?? []).filter((t) => t !== syncTile);

  const scopedPeople = scoped(people.data ?? [], scope);
  const scopedDevices = scoped(devices.data ?? [], scope);

  /* People, not accounts. A shared mailbox on /users is a real licence cost and
     not a member of staff, so it is out of every head-count here. */
  const active = scopedPeople.filter((p) => isRealPerson(p) && p.status === 'Active').length;
  const starters = scopedPeople.filter((p) => isRealPerson(p) && p.status === 'Starter').length;
  /* Housekeeping, across the whole tenant rather than the scoped depot — a
     leaver still holding a licence costs the same wherever they sat, and a
     depot filter would let the number look clean by hiding half of it. */
  const deviceCountByPerson = new Map<string, number>();
  for (const d of devices.data ?? []) {
    if (d.user) deviceCountByPerson.set(d.user, (deviceCountByPerson.get(d.user) ?? 0) + 1);
  }
  const unfinished = (people.data ?? []).filter(
    (p) => offboardingGaps(p, deviceCountByPerson.get(p.id) ?? 0).length > 0,
  );
  const dormant = (people.data ?? []).filter((p) => isDormant(p));

  const inUse = scopedDevices.filter((d) => d.user).length;
  const spare = scopedDevices.filter((d) => d.status.startsWith('Spare')).length;

  /* Licence usage counts assignments across everyone, not just the scoped depot —
     a tenant SKU pool is not divisible by site. */
  const assigned = new Map<string, number>();
  for (const p of people.data ?? []) {
    if (p.status === 'Leaver') continue;
    for (const id of p.lic) assigned.set(id, (assigned.get(id) ?? 0) + 1);
  }
  /* Hidden SKUs are excluded from every total. A free licence nobody uses would
     otherwise inflate the seat count and the idle-spend figure that the whole
     screen is judged by. */
  const countedSkus = visibleLicences(skus.data ?? []);
  const totalUnits = countedSkus.reduce((sum, k) => sum + k.total, 0);
  const usedUnits = countedSkus.reduce((sum, k) => sum + Math.min(k.total, assigned.get(k.id) ?? 0), 0);
  const idleSpend = countedSkus.reduce(
    (sum, k) => sum + Math.max(0, k.total - (assigned.get(k.id) ?? 0)) * k.cost * 12,
    0,
  );

  const expiries = buildExpiries({
    devices: scopedDevices,
    people: scopedPeople,
    suppliers: suppliers.data,
    domains: domains.data,
    applications: applications.data,
    credentials: credentials.data,
    articles: articles.data,
    skus: countedSkus,
  });
  const soon = dueWithin(expiries, 90);

  /* The four things worth interrupting someone about. Ordered by how bad it is
     to find out late rather than by how easy it is to compute. */
  const attention = [
    /* Somebody waiting to be set up, or waiting to be offboarded. FIRST in the
     * list, above the overdue expiries, because this panel shows six things and
     * a run of out-of-warranty laptops would otherwise push them off it.
     *
     * Here at all because the Onboarding queue is the one worklist nobody opens
     * unprompted: a starter arrives on their first morning and the person who
     * should walk them through it is not necessarily the person who notices. A
     * leaver is worse — nothing prompts anybody, and the cost of missing it is
     * somebody keeping access they should have lost, which is a worse morning
     * than an expired warranty. */
    ...scopedPeople
      .filter(isOffboarding)
      .slice(0, 4)
      .map((p) => ({
        id: `off-${p.id}`,
        icon: 'ti-user-minus',
        title: `${p.name} — still has access`,
        sub: p.missingSince
          ? `Deleted in 365 ${fmtDate(p.missingSince)}, and not offboarded`
          : p.leavingOn ? `Leaves ${fmtDate(p.leavingOn)}` : 'Left, and not offboarded',
        tone: 'bad' as const,
        to: '/offboarding',
      })),
    ...scopedPeople
      .filter(isOnboarding)
      .slice(0, 4)
      .map((p) => ({
        id: `on-${p.id}`,
        icon: 'ti-user-plus',
        title: `${p.name} — not set up`,
        sub: isUnclassified(p)
          ? 'Nobody has said what kind of account this is'
          : `${p.title || 'No job title'} · ${p.dept || 'no department'}`,
        tone: 'warn' as const,
        to: '/onboarding',
      })),
    ...(backups.data ?? [])
      .filter((b) => b.ok === false)
      .map((b) => ({
        id: `backup-${b.id}`,
        icon: 'ti-shield-off',
        title: b.name,
        sub: b.rpo === 'No recovery point' ? 'No recovery point at all' : 'Last run failed',
        tone: 'bad' as const,
        to: '/backup-dr',
      })),
    ...soon
      .filter((e) => e.days < 0)
      .slice(0, 4)
      .map((e) => ({
        id: e.id,
        icon: e.icon,
        title: e.label,
        sub: `Overdue — was due ${fmtDate(e.due)}`,
        tone: 'bad' as const,
        to: '/expiries',
      })),
    ...scopedDevices
      .filter((d) => d.compliance === 'Non-compliant')
      .map((d) => ({
        id: `nc-${d.id}`,
        icon: 'ti-device-desktop-exclamation',
        title: `${d.tag} — non-compliant`,
        sub: d.notes || `${d.make} ${d.model}`,
        tone: 'warn' as const,
        to: '/devices',
      })),
    ...soon
      .filter((e) => e.days >= 0 && e.days <= 30)
      .slice(0, 5)
      .map((e) => ({
        id: e.id,
        icon: e.icon,
        title: e.label,
        sub: `Due ${countdown(e.due)} · ${e.detail}`,
        tone: 'warn' as const,
        to: '/expiries',
      })),
  ];

  const loading = people.isLoading || devices.isLoading;

  /* A failed read leaves data undefined, and every count derived from it lands on
     zero. "0 devices" is not a blank — it is a confident, wrong answer, and on the
     landing screen it is the first thing anyone sees. Treat unknown as unknown. */
  const failed = [people, devices, skus].find((q) => q.isError);
  const unknown = loading || !!failed;

  const overdueCount = soon.filter((e) => e.days < 0).length;

  return (
    <div className={s.stack}>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>
            {greeting()}, {user?.name?.split(' ')[0] ?? 'there'}
          </h1>
          <p>Everything IT owns, in one place.</p>
        </div>
        <div className={s.syncStatus}>
          {/* Expired tiles are dropped by the service, so a tile that is here at
              all ran within the last 24 hours — which is why the absent case can
              state a fact rather than hedge. Absence is the alarm: success and
              failure both push a tile, so nothing arriving is the third state
              and the only one that catches a timer that has stopped. */}
          {tiles.isLoading ? null : syncTile ? (
            <Link to="/audit" className={s.syncLine} title="What it changed">
              <Badge tone={syncTile.tone === 'muted' ? 'ok' : syncTile.tone}>
                <i className={`ti ${syncTile.icon}`} aria-hidden /> Synced {syncTile.when}
              </Badge>
              <span className={s.syncDetail}>{syncTile.val}</span>
            </Link>
          ) : (
            <Link to="/audit" className={s.syncLine} title="What it last changed">
              <Badge tone="bad" icon="ti-cloud-off">
                Not synced in 24 hours
              </Badge>
              <span className={s.syncDetail}>The nightly run has not reported</span>
            </Link>
          )}

          {/* Forty seconds is a long time to look at a button that has only
              gone grey. It waits for the run rather than accepting and
              returning — the Functions host may stop an invocation once its
              response is sent — so when it finishes, the data really is synced,
              and until then the least it can do is say how long it has been. */}
          <Button
            icon={sync.isPending ? 'ti-loader-2' : 'ti-cloud-download'}
            spinning={sync.isPending}
            onClick={() => sync.mutate()}
            disabled={!online || sync.isPending}
            title={online ? undefined : 'Sync needs a network'}
          >
            {sync.isPending
              ? `Syncing… ${elapsed}s`
              : 'Sync from Microsoft 365'}
          </Button>
        </div>

        {sync.isError && (
 <div className={`${s.ownedNote} ${s.spaced}`} >
            <i className="ti ti-alert-triangle" aria-hidden />
            <span>{sync.error instanceof Error ? sync.error.message : 'Could not start a sync.'}</span>
          </div>
        )}
      </div>

      {scope !== 'all' && (
        <div className={s.scopebar}>
          <i className="ti ti-filter" aria-hidden />
          <span>
            Showing <b>{svc.depots.name(scope)}</b> only — people, devices and network are filtered.
            Licences, suppliers and contracts stay company-wide.
          </span>
          <button type="button" onClick={() => setScope('all')}>
            Show all depots
          </button>
        </div>
      )}

      {failed && <LoadError error={failed.error} what="the dashboard" />}

      <StatGrid>
        <StatCard
          icon="ti-users"
          label="Active people"
          value={unknown ? '—' : active}
          tone={starters > 0 ? 'info' : 'ok'}
          foot={starters > 0 ? `${starters} starting` : 'All active'}
          /* Opens the list already narrowed to what the card counted: people, not
             accounts, and active, which is what `active` above is. */
          to="/people?status=Active&kind=User"
        />
        <StatCard
          icon="ti-device-laptop"
          label="Assets on the register"
          value={unknown ? '—' : scopedDevices.length}
          tone="ok"
          foot={`${inUse} out · ${spare} spare`}
          to="/devices"
        />
        <StatCard
          icon="ti-license"
          label="Licences assigned"
          value={skus.isLoading || skus.isError ? '—' : `${usedUnits}/${totalUnits}`}
          tone={idleSpend > 600 ? 'warn' : 'ok'}
          foot={idleSpend > 0 ? `${money(idleSpend)}/yr idle` : 'Fully assigned'}
          to="/licences"
        />
        <StatCard
          icon="ti-user-off"
          label="Accounts to tidy up"
          /* Two different problems, one number, because they have the same
             answer: an account nobody has finished with. Splitting them into two
             tiles would halve both and make neither look worth doing. */
          value={unknown ? '—' : unfinished.length + dormant.length}
          tone={unfinished.length > 0 ? 'bad' : dormant.length > 0 ? 'warn' : 'ok'}
          foot={
            unfinished.length + dormant.length === 0
              ? 'Nothing outstanding'
              : `${unfinished.length} mid-offboarding · ${dormant.length} dormant`
          }
          to="/people?lens=tidy"
        />
        <StatCard
          icon="ti-calendar-exclamation"
          label="Expiring within 90 days"
          value={soon.length}
          tone={overdueCount > 0 ? 'bad' : soon.length > 0 ? 'warn' : 'ok'}
          foot={overdueCount > 0 ? `${overdueCount} overdue` : 'Action'}
          to="/expiries?within=90"
        />
      </StatGrid>

      {/* Hidden when there is nothing in it. It used to render a heading over a
          single tile, or over nothing at all. */}
      {otherTiles.length > 0 && (
      <div>
        <div className={s.sectionLabel}>Live status — pushed in by scripts</div>
        <Tiles>
          {otherTiles.map((t) => (
            <Tile
              key={t.id}
              icon={t.icon}
              value={t.val}
              label={t.label}
              foot={`${t.src} · ${t.when}`}
              tone={t.tone === 'ok' || t.tone === 'warn' || t.tone === 'bad' ? t.tone : undefined}
              title={`Pushed by ${t.src}`}
            />
          ))}
        </Tiles>
      </div>
      )}

      {/* Two full-width rows rather than two columns.

          Three panels on the right against two on the left cannot balance:
          the right column ran 1,605px against the left's 930, so Recent
          activity began 1,320px down the page with 677px of white space
          beside it. Nothing suggested it was there, and moving one panel
          across would only have moved the gap.

          So they are grouped by what a panel is for rather than by column.
          The two lists that ask something of you sit together at the top;
          the three you consult sit in a full-width row beneath, and a row
          spanning the page reads as "there is more below" in a way a narrow
          column never does. */}
      <div className={s.dashActs}>
        <Panel
          title="Needs attention"
          flush
          actions={
            <Link to="/expiries" className={s.panelMore}>
              All expiries
            </Link>
          }
        >
          {loading ? (
            <TableSkeleton rows={5} />
          ) : attention.length === 0 ? (
            <EmptyState icon="ti-circle-check" title="Nothing needs you">
              No failed backups, nothing overdue, no non-compliant devices in this scope.
            </EmptyState>
          ) : (
            <div className={s.rows}>
              {attention.slice(0, 6).map((a) => (
                <Link key={a.id} to={a.to} className={s.row}>
                  <span className={s.rowIcon}>
                    <i className={`ti ${a.icon}`} aria-hidden />
                  </span>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{a.title}</div>
                    <div className={s.rowSub}>{a.sub}</div>
                  </div>
                  <Badge tone={a.tone}>{a.tone === 'bad' ? 'Action' : 'Soon'}</Badge>
                </Link>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Next 90 days"
          flush
          actions={
            <Link to="/expiries" className={s.panelMore}>
              View all
            </Link>
          }
        >
          {soon.length === 0 ? (
            <EmptyState icon="ti-calendar-check" title="Clear for 90 days">
              Nothing in any register expires between now and then.
            </EmptyState>
          ) : (
            <div className={s.rows}>
              {soon.slice(0, 6).map((e) => (
                <Link key={e.id} to="/expiries" className={s.row}>
                  <span className={s.rowIcon}>
                    <i className={`ti ${e.icon}`} aria-hidden />
                  </span>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{e.label}</div>
                    <div className={s.rowSub}>{e.source}</div>
                  </div>
                  <Badge tone={expiryTone(e.due)}>{countdown(e.due)}</Badge>
                </Link>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className={s.dashRefs}>
        {/* Estate by depot. The bar is that depot's share of the whole
            register, which is the number that tells you where the kit is. */}
        <Panel
          title="Estate by depot"
          flush
          actions={
            <Link to="/network" className={s.panelMore}>
              Site detail
            </Link>
          }
        >
          <div className={s.padY}>
            {useDepots().map((d) => {
              const dv = (devices.data ?? []).filter((x) => x.loc === d.id).length;
              const pe = headcount((people.data ?? []).filter((x) => x.loc === d.id));
              return (
                <div
                  key={d.id}
                  className={s.meterRow}
                  role="button"
                  tabIndex={0}
                  onClick={() => setScope(d.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setScope(d.id);
                    }
                  }}
                  title={`Scope everything to ${d.name}`}
                >
                  <span className={s.meterRowTop}>
                    <b>{d.name}</b>
                    <span>
                      {dv} assets · {pe} people
                    </span>
                  </span>
                  <Meter value={dv} max={Math.max(1, devices.data?.length ?? 1)} />
                  <small>{locations.data?.[d.id]?.addr || 'No address recorded'}</small>
                </div>
              );
            })}
          </div>
        </Panel>

        {/* Licence utilisation, worst-first: the spare seats are the money. */}
        <Panel
          title="Licence utilisation"
          flush
          actions={
            <Link to="/licences" className={s.panelMore}>
              Manage
            </Link>
          }
        >
          <div className={s.padY}>
            {[...(skus.data ?? [])]
              .sort((a, b) => b.total - (assigned.get(b.id) ?? 0) - (a.total - (assigned.get(a.id) ?? 0)))
              .slice(0, 4)
              .map((k) => {
                const used = Math.min(k.total, assigned.get(k.id) ?? 0);
                const pct = k.total > 0 ? Math.round((used / k.total) * 100) : 0;
                return (
                  <div key={k.id} className={s.meterRow}>
                    <span className={s.meterRowTop}>
                      <b>{k.short}</b>
                      <span>
                        {used}/{k.total}
                      </span>
                    </span>
                    <Meter
                      value={used}
                      max={k.total}
                      tone={pct > 92 ? 'bad' : pct < 60 ? 'warn' : 'info'}
                    />
                    <small>
                      {k.total - used} spare · {money((k.total - used) * k.cost)} per month unused
                    </small>
                  </div>
                );
              })}
          </div>
        </Panel>

        <Panel
          title="Recent activity"
          flush
          actions={
            <Link to="/audit" className={s.panelMore}>
              Full trail
            </Link>
          }
        >
          <div className={s.rows}>
            {(audit.data ?? []).slice(0, 4).map((a, i) => (
              <div key={`${a.when}-${i}`} className={s.row}>
                <span className={s.rowIcon}>
                  <i className={`ti ${a.icon}`} aria-hidden />
                </span>
                <div className={s.rowMain}>
                  <div className={s.rowTitle}>{a.act}</div>
                  <div className={s.rowSub}>{a.obj}</div>
                  <div className={s.rowSub}>
                    {a.who} · {a.when}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
